import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  TabList,
  Tab,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn, type AccountInfo, type ScheduleCBusiness } from '../../../types';
import { searchDataverse } from '../../../services/api';

export interface AddBusinessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAliases: string[];
  /** Existing Schedule C businesses on the return; the new one is appended to this list. */
  currentBusinesses: ScheduleCBusiness[];
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function AddBusinessDialog({
  open,
  onOpenChange,
  currentAliases,
  currentBusinesses,
  updateMutation,
}: AddBusinessDialogProps) {
  const [mode, setMode] = useState<'search' | 'manual'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AccountInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountInfo | null>(null);

  // Manual entry fields
  const [businessName, setBusinessName] = useState('');
  const [ein, setEin] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (open) {
      setMode('search');
      setSearchTerm('');
      setSearchResults([]);
      setSelectedAccount(null);
      setBusinessName('');
      setEin('');
      setPhone('');
    }
  }, [open]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const results = await searchDataverse(searchTerm, 'account');
      setSearchResults(results.accounts || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSave = () => {
    let name: string;
    let businessId: string | undefined;
    let businessEin: string | undefined;
    let businessPhone: string | undefined;

    if (mode === 'search' && selectedAccount) {
      name = selectedAccount.name;
      businessId = selectedAccount.accountId;
      businessPhone = selectedAccount.phone;
    } else if (mode === 'manual' && businessName.trim()) {
      name = businessName.trim();
      businessEin = ein.trim() || undefined;
      businessPhone = phone.trim() || undefined;
    } else {
      return;
    }

    const updatedAliases = [...(currentAliases || [])];
    if (!updatedAliases.includes(name)) {
      updatedAliases.push(name);
    }

    // Append to the existing list (a return may carry several Schedule C businesses). The backend
    // mirrors the primary (first) business into the legacy scalar fields, so we only send the list.
    const newBusiness: ScheduleCBusiness = {
      id: businessId ?? null,
      name,
      ein: businessEin ?? null,
      phone: businessPhone ?? null,
    };
    const dedupedExisting = (currentBusinesses || []).filter(
      (b) => !(businessId && b.id === businessId) &&
        !(b.name && b.name.toLowerCase() === name.toLowerCase())
    );

    const updates: Partial<TaxReturn> = {
      scheduleCBusinesses: [...dedupedExisting, newBusiness],
      searchableAliases: updatedAliases,
    };

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const canSave =
    (mode === 'search' && selectedAccount !== null) ||
    (mode === 'manual' && businessName.trim());

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogBody>
          <DialogTitle>Add Schedule C Business</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <TabList
                selectedValue={mode}
                onTabSelect={(_, data) => setMode(data.value as 'search' | 'manual')}
              >
                <Tab value="search">Search Accounts</Tab>
                <Tab value="manual">Manual Entry</Tab>
              </TabList>

              {mode === 'search' && (
                <>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      style={{ flex: 1 }}
                      placeholder="Search by business name..."
                      value={searchTerm}
                      onChange={(_, data) => setSearchTerm(data.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      contentBefore={<SearchRegular />}
                    />
                    <Button
                      appearance="primary"
                      onClick={handleSearch}
                      disabled={searching || !searchTerm.trim()}
                    >
                      {searching ? <Spinner size="tiny" /> : 'Search'}
                    </Button>
                  </div>
                  {searchResults.length > 0 && (
                    <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {searchResults.map((account) => (
                        <div
                          key={account.accountId}
                          onClick={() => setSelectedAccount(account)}
                          style={{
                            padding: '12px',
                            backgroundColor: selectedAccount?.accountId === account.accountId ? '#e6f2ff' : 'var(--tl-bg-page)',
                            border: selectedAccount?.accountId === account.accountId ? '2px solid #0078d4' : '1px solid var(--tl-border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{account.name}</div>
                          <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                            {account.phone || 'No phone'} {account.primaryContactName && `· ${account.primaryContactName}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.length === 0 && searchTerm && !searching && (
                    <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', textAlign: 'center', padding: '16px' }}>
                      No accounts found. Try a different search or use manual entry.
                    </div>
                  )}
                </>
              )}

              {mode === 'manual' && (
                <>
                  <Field label="Business Name" required>
                    <Input
                      value={businessName}
                      onChange={(_, data) => setBusinessName(data.value)}
                      placeholder="e.g., ABC Consulting LLC"
                    />
                  </Field>
                  <Field label="EIN">
                    <Input
                      value={ein}
                      onChange={(_, data) => setEin(data.value)}
                      placeholder="XX-XXXXXXX"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={phone}
                      onChange={(_, data) => setPhone(data.value)}
                      placeholder="(239) 555-0123"
                    />
                  </Field>
                </>
              )}

              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', padding: '8px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                Link a single-member LLC or Schedule C business to this individual return. The business name will be added to searchable aliases.
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canSave || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Add Business'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
